/* MinerOps Brand — Confident Minimal
   Chart colors chosen for maximum legibility on white backgrounds */
export const STRATEGY_COLORS = ['#2563EB', '#16A34A', '#B8860B', '#DC2626', '#7C3AED', '#DB2777', '#0891B2', '#1A1A1A'];

export const createDefaultStrategy = (name, color) => ({
  id: crypto.randomUUID(),
  name,
  color,
  isBenchmark: false,
  enabled: true,               // hidden from charts/reports when false
  startingMiners: 5,
  hashRate: 270,               // current-gen (S21 XP-class)
  powerDraw: 3645,
  minerPrice: 5500,
  initialMinerLifespan: 60,
  initialResaleValue: 500,
  useInitialFinancing: false,
  initialLoanAmount: 50000,
  initialLoanRate: 0.08,
  initialLoanTerm: 36,
  initialInterestOnly: false,
  initialOpexCoverage: 'btc',
  fundingMode: 'fixed',        // 'fixed' contribution | 'shortfall' bill coverage
  fundingStartMonth: 1,
  fundingEndMonth: 9999,
  otherIncome: 0,
  sellPercent: 0,
  initialCapexIsSunk: false,   // clients purchase their fleet
  autoSellForCashBuffer: false,
  cashBufferMin: 5000,
  expansions: [],
});

export const S = {
  card: {
    background: '#FFFFFF',
    border: '1px solid #D4D4D4',
    borderRadius: '8px',
  },
  innerCard: {
    background: '#F5F5F5',
    border: '1px solid #D4D4D4',
    borderRadius: '4px',
  },
  label: {
    color: '#525252',
    fontSize: '12px',
    textTransform: 'uppercase',
    letterSpacing: '2px',
    fontWeight: 500,
    fontFamily: "'IBM Plex Mono', monospace",
  },
  labelSm: {
    color: '#737373',
    fontSize: '11px',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    fontFamily: "'IBM Plex Mono', monospace",
  },
  muted: { color: '#525252' },
  subtle: { color: '#737373' },
  bright: { color: '#1A1A1A' },
  tnum: { fontVariantNumeric: 'tabular-nums', fontFamily: "'IBM Plex Mono', monospace" },
  tn: (c) => ({ fontVariantNumeric: 'tabular-nums', fontFamily: "'IBM Plex Mono', monospace", color: c }),
  grid: { strokeDasharray: '3 6', stroke: '#E5E5E5' },
  axis: { fontSize: 11, fill: '#737373', fontFamily: "'IBM Plex Mono', monospace" },
  ttip: {
    backgroundColor: '#1A1A1A',
    border: '1px solid #333333',
    borderRadius: '4px',
    color: '#FCFCFC',
    fontFamily: "'IBM Plex Mono', monospace",
  },
  divider: { borderBottom: '1px solid #E5E5E5' },
  dividerLight: { borderBottom: '1px solid #F5F5F5' },
  row: { borderBottom: '1px solid #F5F5F5' },
  C: {
    gold: '#92700C',
    blue: '#2563EB',
    green: '#15803D',
    red: '#DC2626',
    purple: '#7C3AED',
    cyan: '#0E7490',
    black: '#1A1A1A',
    orange: '#92700C',
    yellow: '#92700C',
  },
};

// Mining constants
export const AVG_DAYS_PER_MONTH = 30.4375;
export const TWO_POW_32 = 4294967296;
export const MAX_PROJECTION_MONTHS = 252; // 21 years
export const DEFAULT_UPTIME = 0.96; // 96% — accounts for reboots, maintenance, outages
export const DEFAULT_POOL_FEE = 0.025; // 2.5% — standard FPPS pool fee
export const IO_LOAN_SENTINEL = 9999; // endMonth for interest-only loans (no termination)

// Halving: computed dynamically from block height in marketDataService.
// These are fallback values if block height is unavailable.
export const FALLBACK_HALVING_MONTHS = [25, 73, 121, 169, 217];

export const MACRS_RATES = [0.20, 0.32, 0.192, 0.1152, 0.1152, 0.0576];

// Compute halving months from current block height
// Each halving occurs every 210,000 blocks; ~144 blocks/day
export function computeHalvingMonths(blockHeight) {
  if (!blockHeight || blockHeight <= 0) return FALLBACK_HALVING_MONTHS;
  const currentEra = Math.floor(blockHeight / 210000); // which halving era we're in
  const months = [];
  for (let era = currentEra + 1; era <= currentEra + 5; era++) {
    const targetBlock = era * 210000;
    const blocksAway = targetBlock - blockHeight;
    const daysAway = blocksAway / 144; // avg 144 blocks/day
    const monthsAway = Math.round(daysAway / AVG_DAYS_PER_MONTH);
    months.push(Math.max(1, monthsAway));
  }
  return months;
}

// ═══════ Tax Constants (2026 Tax Year) ═══════
// Updated annually — these reflect enacted law as of 2026

export const SE_TAX = {
  ssWageBase: 184500,        // 2026 SS wage base (projected, ~4.8% increase from 2025 $176,100)
  ssRate: 0.124,             // 12.4% combined SS (6.2% + 6.2%)
  medicareRate: 0.029,       // 2.9% combined Medicare (1.45% + 1.45%)
  additionalMedicareRate: 0.009, // 0.9% Additional Medicare Tax (IRC §3101(b)(2))
  additionalMedicareThreshold: { single: 200000, mfj: 250000 },
  netEarningsFactor: 0.9235, // 92.35% of net SE earnings
  futaRate: 0.006,           // 0.6% net FUTA (after state credit)
  futaWageBase: 7000,        // FUTA applies to first $7,000 per employee
};

// QBI Deduction — IRC §199A (inflation-adjusted for 2026)
export const QBI = {
  rate: 0.20,
  phaseInStart: { single: 200000, mfj: 400000 },   // approximate 2026
  phaseInRange: { single: 50000, mfj: 100000 },     // phase-out window
};

// Excess Business Loss Limitation — IRC §461(l) (2026 projected)
export const EBL_LIMITS = {
  single: 320000,
  mfj: 640000,
};

// Bonus Depreciation — IRC §168(k) as amended by OBBBA (2025)
// 100% through 2029, then phase-down
export const BONUS_DEP_RATES = {
  2025: 1.00, 2026: 1.00, 2027: 1.00, 2028: 1.00, 2029: 1.00,
  2030: 0.80, 2031: 0.60, 2032: 0.40, 2033: 0.20, 2034: 0, 2035: 0, 2036: 0,
};

// C-Corp flat rate
export const CCORP_TAX_RATE = 0.21;

// Tax year and standard deduction (2026)
export const TAX_YEAR = 2026;
export const STANDARD_DEDUCTION = { single: 16150, mfj: 32300 }; // 2026 projected

// FEIE — IRC §911 Foreign Earned Income Exclusion (2026 projected, ~3% from 2025 $130,000)
export const FEIE_EXCLUSION = 133900;

// Section 179 — IRC §179(b)(1) (2026, inflation-adjusted from $1.25M in 2025)
export const SECTION_179_LIMIT = 1290000;

// NIIT — IRC §1411 (not inflation-adjusted)
export const NIIT = {
  rate: 0.038,
  threshold: { single: 200000, mfj: 250000 },
};

// Qualified dividend tax rates by income bracket
export const QUAL_DIVIDEND_RATES = { low: 0, mid: 0.15, high: 0.20 };

// S&P 500 starting price for DCA comparison benchmark
export const SP500_START_PRICE = 6000;

// State income tax rates (top marginal rate, 2026)
// Sources: Tax Foundation, state revenue department publications
export const STATE_TAX_RATES = [
  { code: 'NONE', label: 'No State Tax (TX, FL, WY, etc.)', rate: 0 },
  { code: 'AL', label: 'Alabama', rate: 5.0 },
  { code: 'AK', label: 'Alaska', rate: 0 },
  { code: 'AZ', label: 'Arizona', rate: 2.5 },
  { code: 'AR', label: 'Arkansas', rate: 4.4 },
  { code: 'CA', label: 'California', rate: 13.3 },
  { code: 'CO', label: 'Colorado', rate: 4.4 },
  { code: 'CT', label: 'Connecticut', rate: 6.99 },
  { code: 'DE', label: 'Delaware', rate: 6.6 },
  { code: 'FL', label: 'Florida', rate: 0 },
  { code: 'GA', label: 'Georgia', rate: 5.39 },
  { code: 'HI', label: 'Hawaii', rate: 11.0 },
  { code: 'ID', label: 'Idaho', rate: 5.8 },
  { code: 'IL', label: 'Illinois', rate: 4.95 },
  { code: 'IN', label: 'Indiana', rate: 3.05 },
  { code: 'IA', label: 'Iowa', rate: 5.7 },
  { code: 'KS', label: 'Kansas', rate: 5.7 },
  { code: 'KY', label: 'Kentucky', rate: 4.0 },
  { code: 'LA', label: 'Louisiana', rate: 4.25 },
  { code: 'ME', label: 'Maine', rate: 7.15 },
  { code: 'MD', label: 'Maryland', rate: 5.75 },
  { code: 'MA', label: 'Massachusetts', rate: 9.0 },
  { code: 'MI', label: 'Michigan', rate: 4.25 },
  { code: 'MN', label: 'Minnesota', rate: 9.85 },
  { code: 'MS', label: 'Mississippi', rate: 5.0 },
  { code: 'MO', label: 'Missouri', rate: 4.95 },
  { code: 'MT', label: 'Montana', rate: 5.9 },
  { code: 'NE', label: 'Nebraska', rate: 5.84 },
  { code: 'NV', label: 'Nevada', rate: 0 },
  { code: 'NH', label: 'New Hampshire', rate: 0 },
  { code: 'NJ', label: 'New Jersey', rate: 10.75 },
  { code: 'NM', label: 'New Mexico', rate: 5.9 },
  { code: 'NY', label: 'New York', rate: 10.9 },
  { code: 'NC', label: 'North Carolina', rate: 4.5 },
  { code: 'ND', label: 'North Dakota', rate: 2.5 },
  { code: 'OH', label: 'Ohio', rate: 3.5 },
  { code: 'OK', label: 'Oklahoma', rate: 4.75 },
  { code: 'OR', label: 'Oregon', rate: 9.9 },
  { code: 'PA', label: 'Pennsylvania', rate: 3.07 },
  { code: 'RI', label: 'Rhode Island', rate: 5.99 },
  { code: 'SC', label: 'South Carolina', rate: 6.4 },
  { code: 'SD', label: 'South Dakota', rate: 0 },
  { code: 'TN', label: 'Tennessee', rate: 0 },
  { code: 'TX', label: 'Texas', rate: 0 },
  { code: 'UT', label: 'Utah', rate: 4.65 },
  { code: 'VT', label: 'Vermont', rate: 8.75 },
  { code: 'VA', label: 'Virginia', rate: 5.75 },
  { code: 'WA', label: 'Washington', rate: 0 },
  { code: 'WV', label: 'West Virginia', rate: 5.12 },
  { code: 'WI', label: 'Wisconsin', rate: 7.65 },
  { code: 'WY', label: 'Wyoming', rate: 0 },
  { code: 'DC', label: 'Washington D.C.', rate: 10.75 },
];

// States that do NOT conform to federal bonus depreciation (IRC §168(k))
// These states require standard MACRS depreciation for state tax purposes
export const NON_CONFORMING_BONUS_DEP_STATES = new Set([
  'CA', 'NY', 'NJ', 'PA', 'AR', 'HI', 'MD', 'MS',
]);

// Price growth scenarios (annual rates)
export const GROWTH_RATES = { 1: 0.05, 2: 0.15, 3: 0.30, 4: 0.75 };
export const SCENARIO_LABELS = { 1: 'Bear +5%/yr', 2: 'Base +15%/yr', 3: 'Bull +30%/yr', 4: 'Historical +75%/yr' };

// Cumulative ROI to approximate annualized rate %. Understates true
// money-weighted return when contributions are staged over time.
export const annualizedROI = (roi, years) =>
  roi > -100 && years > 0 ? (Math.pow(1 + roi / 100, 1 / years) - 1) * 100 : null;

/* Long-run difficulty growth used for planning, as an annual rate.

   This is a stated assumption, not a forecast, and deliberately not derived
   from trailing data. A 3-year log-linear fit returned 37% in mid-2026 while
   difficulty was actually falling — the window was dominated by the 2023-2025
   build-out and could not see that the trend had broken:

     2023  +37.4%    trailing 3yr   +34.6%/yr
     2024  +50.0%    trailing 2yr   +18.2%/yr
     2025  +34.2%    trailing 1yr    -1.1%/yr
     2026  -13.8%    (difficulty fell 148.3T to 126.2T)

   Compounding 37% across a 15-year projection implies a 114x rise in network
   difficulty, which no energy budget supports. It made every plan lose to
   simply buying Bitcoin, by 2-3x.

   10% is defensible to a CPA precisely because it is a stated conservative
   input with sensitivity shown against it, rather than a number that happens
   to fall out of whichever window was chosen. Users can override it, and the
   observed trailing rate is displayed next to the field for context. */
export const PLANNING_DIFFICULTY_GROWTH = 0.10;
